import { action, computed, makeAutoObservable, observable } from "mobx"
import FeatureFlagsService from "../../featureFlags/FeatureFlagsService"
import { MainApi } from "../../apis/MainApi"
import { TriageCustomer } from "../../models/TriageCustomer"
import { TriageProduct } from "../../models/TriageProduct"
import { ConfigItem } from "../../models/ConfigItem"
import {
    IngestPayload,
    IngestFile,
    CachedItem,
    TenantConfig,
    CustomPromptAsk,
    ObservationSuggestionModel,
    HighlightSuggestionModel,
    ObservationSuggestionsResponse,
    HighlightSuggestionsResponse,
    ObservationsSuggestionsUpsertResponse,
    HighlightSuggestionsUpsertResponse,
    GeneralResponse,
    UpdateSuggestionModel,
    HighlightSuggestionUpdateRequest,
    TagConfig,
    Tag,
    DocumentTag,
    TagValueGroup,
    TagValueGroupUpsertItem,
    PartListItem
} from "./models"
import Backend from "../../apis/KABackend"
import { activeIngestStatus, PENDING_DELETION_STATUS, unselectableStatus } from "./types"
import { Settings } from "../../Settings"
import { buildTagConfigByIndex, buildTagConfigByLabel } from "./widgets/tag management/tagConfigLookups"

const PARTS_PAGE_SIZE = 1000
const PARTS_DELETE_CHUNK = 1000

export class CopilotStore {
    mainApi?: MainApi
    backend?: Backend
    configuration?: ConfigItem[]
    user_email: string = ''
    featureFlags:any

    private triageCustomers: TriageCustomer[] = []
    private triageProducts: TriageProduct[] = []
    private files: IngestFile[] = []
    private fileIds: Set<string> = new Set()
    private filesFetchGeneration: number = 0
    private appSettings?: Settings

    @observable selectedCustomer?: TriageCustomer
    @observable selectedModel?: Tag
    @observable selectedFiles: IngestFile[] = []
    @observable systemPrompt: string[] = []
    @observable customPrompts: CustomPromptAsk[] = []
    @observable tagConfigs: TagConfig[] = []
    @observable tagValues: Tag[] = []
    @observable tagValueGroups: TagValueGroup[] = []

    @observable tagValuesLoading: boolean = false
    @observable tagValueGroupsLoading: boolean = false

    @observable productsLoading: boolean = false
    @observable filesLoading: boolean = false
    @observable customPromptLoading: boolean = false

    @observable tenantConfig?: TenantConfig
    @observable isTenantProd: boolean = false


    constructor(api: MainApi, appSettings?:Settings) {
        makeAutoObservable(this)
        this.mainApi = api
        this.featureFlags = FeatureFlagsService.getAllFlags()
        this.appSettings = appSettings
    }

    @computed get customers() {
        return this.triageCustomers
    }

    @computed get region() {
        return this.appSettings?.env.REACT_APP_REGION === 'EU' ? 'EU' : 'US'
    }

    @computed get TriageProducts() {
        return this.triageProducts
    }

    @computed get models() {
        const modelConfig = this.tagConfigs.find(
            (c) => String(c.tag_label).toLowerCase() === "model"
        );
        if (!modelConfig) return [];
        const tagIndex =
            typeof modelConfig.tag_index === "string"
                ? parseInt(modelConfig.tag_index, 10)
                : modelConfig.tag_index;
        if (Number.isNaN(tagIndex)) return [];
        return this.activeTagValuesByTagIndex.get(tagIndex) ?? []
    }

    @computed get tagValueByRowId() {
        const lookup = new Map<string, Tag>()
        this.tagValues.forEach((tagValue) => {
            lookup.set(tagValue.rowid, tagValue)
        })
        return lookup
    }

    @computed get activeTagValues() {
        return this.tagValues.filter((tagValue) => tagValue.is_active !== false)
    }

    @computed get activeTagValuesByTagIndex() {
        const lookup = new Map<number, Tag[]>()
        this.activeTagValues.forEach((tagValue) => {
            const values = lookup.get(tagValue.tag_index) ?? []
            values.push(tagValue)
            lookup.set(tagValue.tag_index, values)
        })
        return lookup
    }

    @computed get tagValuesByTagIndexAndValue() {
        const lookup = new Map<string, Tag[]>()
        this.tagValues.forEach((tagValue) => {
            const key = `${tagValue.tag_index}|${(tagValue.value ?? '').trim().toLowerCase()}`
            const values = lookup.get(key) ?? []
            values.push(tagValue)
            lookup.set(key, values)
        })
        return lookup
    }


    @computed get tagConfigByIndex() {
        return buildTagConfigByIndex(this.tagConfigs)
    }

    @computed get tagConfigByLabel() {
        return buildTagConfigByLabel(this.tagConfigs)
    }

    @computed get ingestFiles() {
        return this.files
    }

    @computed get selectedFilesIds() {
        return this.selectedFiles?.map(row => row.id)
    }

    @computed get fileById() {
        const lookup = new Map<string, IngestFile>()
        this.files.forEach((file) => {
            lookup.set(file.id, file)
        })
        return lookup
    }

    @computed get filesByDocumentName() {
        const lookup = new Map<string, IngestFile[]>()
        this.files.forEach((file) => {
            const values = lookup.get(file.document_name) ?? []
            values.push(file)
            lookup.set(file.document_name, values)
        })
        return lookup
    }

    @computed get fileByDocumentNameOrId() {
        const lookup = new Map<string, IngestFile>()
        this.files.forEach((file) => {
            const key = file.document_name || file.id
            if (!lookup.has(key)) {
                lookup.set(key, file)
            }
        })
        return lookup
    }

    @computed get fileStatusCounts() {
        const counts = {
            pending: 0,
            processing: 0,
            failed: 0,
            done: 0,
            pendingDeletion: 0,
        }
        this.files.forEach((file) => {
            if (file.status === 'Uploaded') {
                counts.pending += 1
            } else if (activeIngestStatus.includes(file.status)) {
                counts.processing += 1
            } else if (file.status === 'Failed') {
                counts.failed += 1
            } else if (file.status === 'Done') {
                counts.done += 1
            } else if (file.status === PENDING_DELETION_STATUS) {
                counts.pendingDeletion += 1
            }
        })
        return counts
    }

    @computed get documentCountsByTagValueId() {
        const counts = new Map<string, number>()
        this.files.forEach((file) => {
            const tagValueIdsForDocument = new Set<string>()
            file.tags?.forEach((tag) => {
                tagValueIdsForDocument.add(tag.rowid)
            })
            tagValueIdsForDocument.forEach((tagValueId) => {
                counts.set(tagValueId, (counts.get(tagValueId) ?? 0) + 1)
            })
        })
        return counts
    }

    @computed get duplicateFilesByDocumentName() {
        const lookup = new Map<string, IngestFile[]>()
        this.filesByDocumentName.forEach((files, documentName) => {
            if (files.length > 1) {
                lookup.set(documentName, files)
            }
        })
        return lookup
    }

    @computed get duplicateFiles() {
        const files: IngestFile[] = []
        this.duplicateFilesByDocumentName.forEach((duplicates) => {
            files.push(...duplicates)
        })
        return files
    }

    @action
    setTriageCustomers = (customers: TriageCustomer[]) => {
        this.triageCustomers = customers
    }

    @action
    setTriageProducts = (products: TriageProduct[]) => {
        this.triageProducts = products
    }

    @action
    setSelectedFiles = (selectedFiles:IngestFile[]) => {
        this.selectedFiles = selectedFiles
    }

    @action
    setIsTenantProd = (isTenantProd: boolean) => {
        this.isTenantProd = isTenantProd
    }

    @action
    setSelectedCustomer = async (id?: string) => {
        if (this.selectedCustomer?.id === id) return;

        this.selectedModel = undefined
        this.clearFiles()
        this.setTagConfigs([])
        this.setTagValues([])
        this.selectedCustomer = this.triageCustomers.find(tc => tc.id === id)
        if (this.selectedCustomer) {
            const customerName = this.selectedCustomer.name
            // set tenant info in db if not already set
            this.saveTenantInfo(customerName, this.selectedCustomer.organization_id ?? '').then(data=>{
                this.isTenantProd = data.isProd
            })
            this.fetchTenantConfig(customerName)
            this.getSystemPrompt(customerName)
            this.loadFilesInBackground()
            this.fetchTagConfigs()
            this.fetchTagValues()
            await this.getAllPrompts(customerName)
        }
    }

    @action
    setTagConfigs = (configs: TagConfig[]) => {
        this.tagConfigs = configs
    }

    @action
    setTagValues = (values: Tag[]) => {
        this.tagValues = values
    }

    fetchTagConfigs = async () => {
        try {
            const configs = await this.backend!.getTagConfigs(this.selectedCustomer!.name)
            this.setTagConfigs(configs)
        } catch (error) {
            console.error('Error fetching tag configs:', error)
            this.setTagConfigs([])
        }
    }

    upsertTagConfigs = async (tag_configs: TagConfig[]) => {
        return await this.backend!.upsertTagConfigs(this.selectedCustomer!.name, tag_configs)
    }

    fetchTagValues = async () => {
        if (!this.backend || !this.selectedCustomer) return [];
        const tenant_id = this.selectedCustomer!.name;
        const BATCH_SIZE = 100000;
        this.tagValuesLoading = true;
        this.setTagValues([]);
        const allValues: Tag[] = [];
        let offset = 0;
        try {
            for (;;) {
                const res = await this.backend!.getTagValues(tenant_id, BATCH_SIZE, offset);
                const batch = res.tag_values ?? [];
                if (batch.length > 0) {
                    allValues.push(...batch);
                }
                if (!res.has_next) break;
                offset += BATCH_SIZE;
                await new Promise((r) => setTimeout(r, 0));
            }
            this.setTagValues([...allValues]);
            return allValues;
        } catch (error) {
            console.error('Error fetching tag values:', error);
            this.setTagValues([]);
            return [];
        } finally {
            this.tagValuesLoading = false;
        }
    }

    upsertTagValues = async (tag_values: Tag[]) => {
        return await this.backend!.upsertTagValues(this.selectedCustomer!.name, tag_values)
    }

    @action
    setTagValueGroups = (groups: TagValueGroup[]) => {
        this.tagValueGroups = groups
    }

    fetchTagValueGroups = async () => {
        if (!this.backend || !this.selectedCustomer) {
            this.setTagValueGroups([])
            return
        }
        this.tagValueGroupsLoading = true
        try {
            const res = await this.backend!.getTagValueGroups(this.selectedCustomer!.name)
            this.setTagValueGroups(res.groups ?? [])
        } catch (error) {
            console.error('Error fetching tag value groups:', error)
            this.setTagValueGroups([])
        } finally {
            this.tagValueGroupsLoading = false
        }
    }

    upsertTagValueGroups = async (groups: TagValueGroupUpsertItem[], replace_existing: boolean = false) => {
        return await this.backend!.upsertTagValueGroupsChunked(this.selectedCustomer!.name, groups, replace_existing)
    }

    deleteTagValueGroups = async (group_ids: string[]) => {
        return await this.backend!.deleteTagValueGroups(this.selectedCustomer!.name, group_ids)
    }

    updateDocumentTags = async (tags_to_remove?: DocumentTag[], tags_to_add?: DocumentTag[]) => {
        return await this.backend!.updateDocumentTags(this.selectedCustomer!.name, tags_to_remove, tags_to_add)
    }

    updateDocumentTagsWhere = async (add_tag_value_ids:string[], where_tag_value_id:string) => {
        return await this.backend!.updateDocumentTagsWhere(this.selectedCustomer!.name, add_tag_value_ids, where_tag_value_id)
    }

    updateUsersPersona = async (update_to_persona_id: string, filter_by_persona_id: string) => {
        return await this.backend!.updateUsersPersona(this.selectedCustomer!.name, update_to_persona_id, filter_by_persona_id)
    }

    @action
    setTenantConfig = (config?: TenantConfig) => {
        this.tenantConfig = config
    }

    @action
    setSelectedModel = (id?: string) => {
        if (this.selectedModel?.rowid === id) return;
        this.clearFiles()
        this.selectedModel = this.models.find((t) => t.rowid === id)
    }

    @action
    addFiles = (newFiles: IngestFile[]) => {
        const uniqueNewFiles = newFiles.filter(file => {
            if (this.fileIds.has(file.id)) return false
            this.fileIds.add(file.id)
            return true
        })
        // Add files to the end (preserve loading order)
        this.files = [...this.files, ...uniqueNewFiles]
    }

    @action
    addUploadedFiles = (newFiles: IngestFile[]) => {
        const uniqueNewFiles = newFiles.filter(file => {
            if (this.fileIds.has(file.id)) return false
            this.fileIds.add(file.id)
            return true
        })
        // Add uploaded files at the beginning of the array (top of the list)
        this.files = [...uniqueNewFiles, ...this.files]
    }

    @action
    setFiles = (files: IngestFile[]) => {
        this.files = files
        this.fileIds = new Set(files.map(file => file.id))
    }

    @action
    clearFiles = () => {
        this.filesFetchGeneration += 1
        this.setFiles([])
        this.selectedFiles = []
    }

    @action
    setTriageProductsLoading = (loading: boolean) => {
        this.productsLoading = loading
    }

    @action
    setFilesLoading = (loading: boolean) => {
        this.filesLoading = loading
    }

    @action
    setCustomPromptLoading = (loading: boolean) => {
        this.customPromptLoading = loading
    }

    @action
    setSystemPrompt = (sysPrompt: string[] = []) => {
        this.systemPrompt = sysPrompt
    }

    @action
    setCustomPrompts = (customPromptsList: CustomPromptAsk[]) => {
        this.customPrompts = customPromptsList
    }

    initStore = (access_token: () => string | undefined, user_email: string, ka_base_url: string, config?: ConfigItem[]) => {
        this.configuration = config
        this.backend = new Backend(ka_base_url, access_token)
        this.user_email = user_email
        this.fetchTriageCustomers(config)
        this.fetchTriageProducts()
    }

    refreshStore = async () => {
        this.fetchTriageCustomers(this.configuration)
        this.fetchTriageProducts()
    }

    setConfiguration = (config?: ConfigItem[]) => {
        this.configuration = config
        if (this.triageCustomers)
            this.setTriageSchemaConfig(this.triageCustomers, config)
    }

    fetchTriageCustomers = async (config?: ConfigItem[]) => {
        this.mainApi!.getTriageCustomers(this.region).then((items) => {
            this.setTriageCustomers(items)
            this.setTriageSchemaConfig(items, config)
        })
    }

    fetchTriageProducts = async () => {
        this.setTriageProductsLoading(true)
        this.mainApi!.getTriageProducts(this.region).then((items) => {
            this.setTriageProducts(items)
            this.setTriageProductsLoading(false)
        })
    }

    fetchTenantConfig = async (tenant_name: string) => {
        const response = await this.backend!.getTenantConfig(tenant_name)
        if (response !== null) {
            this.setTenantConfig(response)
        } else {
	        this.setTenantConfig(undefined)
	    }
    }

    saveTenantConfig = async (tenant_name: string, config: TenantConfig) => {
        const response = await this.backend!.saveTenantConfig(tenant_name, config, this.user_email)
        this.setTenantConfig(response)
    }

    saveTenantInfo = async (tenant_name: string, sf_org_id?: string, isProd?: boolean) => {
        if (sf_org_id === undefined && isProd === undefined) {
            console.log(`Tenant info (payload) for tenant ${tenant_name} is undefined. Request for the change won't be sent`);
            return
        } else {
            let tenant_type = undefined
            if (isProd !== undefined) {
                tenant_type = isProd ? "Prod" : "Dev";
            }
            return await this.backend!.saveTenantInfo(tenant_name, sf_org_id, tenant_type)
        }
    }

    getIngestFilesIncremental = async (customerName: string) => {
        const DOCUMENTS_BATCH_SIZE = 10000
        let offset = 0
        let response = await this.backend!.getFiles(customerName, offset, DOCUMENTS_BATCH_SIZE)
        let allDocuments: IngestFile[] = []
        
        // Process first page
        if (response.documents && response.documents.length > 0) {
            allDocuments = [...response.documents]
        }
        
        // Process subsequent pages
        while (response.has_next) {
            const batchLength = response.documents?.length ?? 0
            if (batchLength === 0) break
            offset += batchLength
            response = await this.backend!.getFiles(customerName, offset, DOCUMENTS_BATCH_SIZE)
            if (response.documents && response.documents.length > 0) {
                allDocuments.push(...response.documents)
                await new Promise(resolve => setTimeout(resolve, 0))
            } else {
                break
            }
        }
        
        return allDocuments
    }

    fetchIngestFilesIncremental = async () => {
        if (!this.selectedCustomer) return []
        const customerName = this.selectedCustomer.name
        const fetchGeneration = this.filesFetchGeneration + 1
        this.filesFetchGeneration = fetchGeneration
        this.setFilesLoading(true)
        
        try {
            const documents = await this.getIngestFilesIncremental(customerName)
            if (fetchGeneration === this.filesFetchGeneration && this.selectedCustomer?.name === customerName) {
                this.setFiles(documents)
            }
            return documents
        } catch (error) {
            console.error('Error fetching files', error)
            return []
        } finally {
            if (fetchGeneration === this.filesFetchGeneration) {
                this.setFilesLoading(false)
            }
        }
    }



    @action
    addIngestFiles = async (fileNames: Array<string>) => {
        // Add files to backend
        const response = await this.backend!.addFiles(this.selectedCustomer!.name, fileNames, this.user_email)
        await this.fetchIngestFilesIncremental()
        
        return response
    }



    @action
    deleteIngestFiles = async (files: IngestFile[]) => {
        // Delete files from backend
        const response = await this.backend!.deleteFiles(this.selectedCustomer!.name, files.map(f => f.id), this.user_email)
        
        // The BE marks documents 'Pending Deletion' and hands the actual removal to a task,
        // so keep the rows and reflect that status until a refresh no longer returns them.
        if (response && response.status === 200) {
            const deletedIds = new Set(files.map(f => f.id))
            this.files.forEach(f => {
                if (deletedIds.has(f.id)) f.status = PENDING_DELETION_STATUS
            })
            this.files = [...this.files]

            // Remove from selected files if they were selected
            this.selectedFiles = this.selectedFiles.filter(f => !deletedIds.has(f.id))
        } else {
            console.error('File deletion failed:', response)
            throw new Error('Failed to delete files from backend')
        }
        
        return response
    }

    postSyncStorage = async (customerName: string) => {
        return await this.backend!.postSyncStorage(customerName)
    }

    copilotIngest = async (customerName: string, ingest_payload: IngestPayload) => {
        return await this.backend!.ingest(customerName, ingest_payload)
    }

    ingestAllPartImages = async (reingest_existing: boolean) => {
        return await this.backend!.ingestPartImages(this.selectedCustomer!.name, reingest_existing)
    }

    // genai-be has no tenant-wide parts delete, so collect every id first (paging is stable
    // while nothing is being deleted yet), then delete in chunks to stay under the body limit.
    private collectAllParts = async (has_images?: boolean): Promise<PartListItem[]> => {
        const tenant_id = this.selectedCustomer!.name
        const parts: PartListItem[] = []
        let offset = 0
        while (true) {
            const page = await this.backend!.listParts(tenant_id, PARTS_PAGE_SIZE, offset, has_images)
            parts.push(...page.parts)
            if (!page.has_next || page.parts.length === 0) break
            offset += page.parts.length
        }
        return parts
    }

    deleteAllParts = async (onProgress?: (done: number, total: number) => void) => {
        const tenant_id = this.selectedCustomer!.name
        const ids = (await this.collectAllParts()).map(p => p.id)
        let deleted = 0
        for (let i = 0; i < ids.length; i += PARTS_DELETE_CHUNK) {
            const chunk = ids.slice(i, i + PARTS_DELETE_CHUNK)
            deleted += (await this.backend!.deleteParts(tenant_id, chunk)).deleted_count ?? 0
            onProgress?.(i + chunk.length, ids.length)
        }
        return { deleted, total: ids.length }
    }

    deleteAllPartImages = async (onProgress?: (done: number, total: number) => void) => {
        const tenant_id = this.selectedCustomer!.name
        const rowids = (await this.collectAllParts(true)).flatMap(p => p.images.map(image => image.rowid))
        let deleted = 0
        for (let i = 0; i < rowids.length; i += PARTS_DELETE_CHUNK) {
            const chunk = rowids.slice(i, i + PARTS_DELETE_CHUNK)
            await this.backend!.deletePartImages(tenant_id, chunk)
            deleted += chunk.length
            onProgress?.(deleted, rowids.length)
        }
        return { deleted, total: rowids.length }
    }

    fetchKaUploadUrlByFileName = async (fileNames: Array<string>) => {
        const response = this.backend!.getUploadUrls(this.selectedCustomer!.name, fileNames, this.user_email)
        return response
    }

    fetchCacheItems = async (tenant_name: string, modelName: string) => {
        const response = this.backend!.getCacheItems(tenant_name, modelName)
        return response
    }

    editCacheItems = async (tenant_name: string, modelName: string, cachedItems:Array<CachedItem>) => {
        const response = this.backend!.editCacheItems(tenant_name, modelName, cachedItems)
        return response
    }

    deleteCacheItems = async (tenant_name: string, modelName: string, toDeleteIds: string[]) => {
        const response = this.backend!.deleteCacheItems(tenant_name, modelName, toDeleteIds)
        return response
    }

    getSystemPrompt = async (tenant_id: string) => {
        const response = await this.backend!.getSystemPrompt(tenant_id)
        if (response !== null) {
            this.setSystemPrompt(response.result? response.result: [])
        }
    }

    getAllPrompts = async (tenant_id: string, refresh: boolean = false) => {
        if (!refresh) this.setCustomPrompts([]) // clear current prompt list before proceeding
        this.setCustomPromptLoading(true)
        const response = await this.backend!.getAllPrompts(tenant_id)
        this.setCustomPrompts(response ?? [])
        this.setCustomPromptLoading(false)
    }

    getAllModelsWithPrompt = () => {
        let result: CustomPromptAsk[] = this.customPrompts.filter(prompt => prompt.prompt_scope === 'tag')
        return result.map(prompt => prompt.tag_id)
    }

    @action
    resetSelections = () => {
        this.triageCustomers = []
        this.triageProducts = []

        this.setSelectedCustomer(undefined)
        this.setSelectedModel(undefined)
    }

    @action
    refreshFiles = async () => {
        this.clearFiles()
        return await this.fetchIngestFilesIncremental()
    }

    @action
    loadFilesInBackground = () => {
        if (!this.selectedCustomer) return
        
        // Load files in background without blocking UI
        setTimeout(() => {
            this.fetchIngestFilesIncremental().catch(error => {
                console.error('Error loading files in background:', error)
            })
        }, 0)
    }



    @action
    refreshFileStatus = async () => {
        if (!this.selectedCustomer) return
        
        try {
            const documents = await this.getIngestFilesIncremental(this.selectedCustomer.name)
            const updatedFileById = new Map<string, IngestFile>()
            documents.forEach((file) => {
                updatedFileById.set(file.id, file)
            })
            
            // Update file statuses in place without full reload
            for (const file of this.files) {
                const updatedFile = updatedFileById.get(file.id)
                if (updatedFile) {
                    file.status = updatedFile.status
                    file.offline_ready = updatedFile.offline_ready
                }
            }
            
            // Remove files from selection if they're no longer selectable
            this.selectedFiles = this.selectedFiles.filter(f => {
                const file = this.files.find(storeFile => storeFile.id === f.id)
                return !file || !unselectableStatus.includes(file.status)
            })
            
            // Trigger reactivity by updating the array reference
            this.files = [...this.files]
        } catch (error) {
            console.error('Error refreshing file status:', error)
            throw error
        }
    }

    private setTriageSchemaConfig(items: TriageCustomer[], config?: ConfigItem[]) {
        if (config) {
            const index = config.findIndex(item => item.name === 'triage_schema')
            if (index !== -1) {
                config[index].options = items.slice().sort((a, b) => a.name.localeCompare(b.name));
                config[index].value = config[index].value ?
                    items.find(i => i.name === config![index].value.name) :
                    config[index].value
            }
        }
    }

    @action
    fetchObservationSuggestions = async (tenant_id: string, model_name: string | null): Promise<ObservationSuggestionsResponse> => {
        if (!this.backend) return { data: [], total_count: 0, total_pages: 0 };

        try {
            const response = await this.backend.getObservationSuggestions(tenant_id, model_name);
            return response;
        } catch (error) {
            console.error('Error fetching observation suggestions:', error);
            return { data: [], total_count: 0, total_pages: 0 };
        }
    }

    @action
    upsertObservationSuggestions = async (tenant_id: string, model_name: string | null, createdItems: Array<ObservationSuggestionModel>,
         updatedItems: Array<ObservationSuggestionModel>): Promise<ObservationsSuggestionsUpsertResponse> => {
        if (!this.backend) return { added: [], updated: [] };

        try {
            const response = await this.backend.upsertObservationSuggestions(tenant_id, model_name, createdItems, updatedItems);
            return response;
        } catch (error) {
            console.error('Error upserting observation suggestions:', error);
            return { added: [], updated: [] };
        }
    }

    @action
    deleteObservationSuggestions = async (tenant_id: string, model_name: string | null, rowids: string[]): Promise<GeneralResponse> => {
        if (!this.backend) return { status: 'error', message: 'Backend not initialized' };

        try {
            const response = await this.backend.deleteObservationSuggestions(tenant_id, model_name, rowids);
            return response;
        } catch (error) {
            console.error('Error deleting observation suggestions:', error);
            return { status: 'error', message: 'Failed to delete observation suggestions' };
        }
    }

    @action
    fetchHighlightSuggestions = async (tenant_id: string, model_name: string): Promise<HighlightSuggestionsResponse> => {
        if (!this.backend) return { data: [], total_count: 0, total_pages: 0 };

        try {
            const response = await this.backend.getHighlightSuggestions(tenant_id, model_name);
            return response;
        } catch (error) {
            console.error('Error fetching highlight suggestions:', error);
            return { data: [], total_count: 0, total_pages: 0 };
        }
    }

    @action
    updateHighlightSuggestions = async (tenant_id: string, model_name: string, items: Array<HighlightSuggestionModel>): Promise<HighlightSuggestionsUpsertResponse> => {
        if (!this.backend) return { updated: [] };

        try {
            // Convert to UpdateSuggestionModel format
            const updateItems: UpdateSuggestionModel[] = items.map(item => ({
                rowid: item.rowid!,
                suggestion: item.suggestion
            }));
            const req: HighlightSuggestionUpdateRequest = {
                update: updateItems
            }

            const response = await this.backend.updateHighlightSuggestions(tenant_id, model_name, req);
            // Convert response back to HighlightSuggestionModel format
            return {
                updated: response.updated.map(item => ({
                    rowid: item.rowid,
                    suggestion: item.suggestion,
                    highlight: '', // These fields will be populated from the existing items
                    chat_answer: ''
                }))
            };
        } catch (error) {
            console.error('Error updating highlight suggestions:', error);
            return { updated: [] };
        }
    }

    @action
    deleteHighlightSuggestions = async (tenant_id: string, model_name: string, rowids: string[]): Promise<GeneralResponse> => {
        if (!this.backend) return { status: 'error', message: 'Backend not initialized' };

        try {
            const response = await this.backend.deleteHighlightSuggestions(tenant_id, model_name, rowids);
            return response;
        } catch (error) {
            console.error('Error deleting highlight suggestions:', error);
            return { status: 'error', message: 'Failed to delete highlight suggestions' };
        }
    }
}

